import { getRequestHeaders } from "./headers";
import { handleError, apiFetch, Item, API_BASE } from './api';

export interface ShiftPassdownEntry {
    pk: string;
    gsi1pk?: string;
    gsi1sk?: string;
    passdownId: string;
    category?: string;
    shift?: string;
    notableCases?: string;
    bolosPEs?: string;
    oldestRespondCases?: string;
    oldestReviewCases?: string;
    escalationsNextSteps?: string;
    issuesBlockers?: string;
    nextForgeAudits?: string;
    // Tiptap ProseMirror JSON mirrors of the Global rich-text fields (preferred
    // for rendering when present; the strings above remain for legacy records).
    notableCasesJson?: Record<string, any>;
    bolosPEsJson?: Record<string, any>;
    oldestRespondCasesJson?: Record<string, any>;
    oldestReviewCasesJson?: Record<string, any>;
    escalationsNextStepsJson?: Record<string, any>;
    issuesBlockersJson?: Record<string, any>;
    nextForgeAuditsJson?: Record<string, any>;
    csStaffPresent?: number;
    csStaffRequired?: number;
    csirStaffPresent?: number;
    csirStaffRequired?: number;
    priorityAlarmsStaffPresent?: number;
    priorityAlarmsStaffRequired?: number;
    nonPriorityAlarmsStaffPresent?: number;
    nonPriorityAlarmsStaffRequired?: number;
    lsocStaffPresent?: number;
    lsocStaffRequired?: number;
    // CS-specific fields
    openCaseIds?: string;
    csEscalations?: string;
    csBOLOs?: string;
    welfareChecks?: string;
    noteworthyCases?: string;
    // Tiptap ProseMirror JSON mirrors of the CS rich-text fields.
    openCaseIdsJson?: Record<string, any>;
    csEscalationsJson?: Record<string, any>;
    csBOLOsJson?: Record<string, any>;
    welfareChecksJson?: Record<string, any>;
    noteworthyCasesJson?: Record<string, any>;
    abandonedCalls?: number;
    avgSpeedOfAnswer?: number;
    // CSIR-specific fields
    csirNotableCases?: string;
    csirCasesInReview?: number;
    csirOpenPlumaBolosPEs?: string;
    csirOpenSimTs?: string;
    csirOpenAlertMediaMessages?: string;
    csirUpcomingCalls?: string;
    csirStaffingFlags?: string;
    csirIssuesFlags?: string;
    csirNextSupervisor?: string;
    // Tiptap ProseMirror JSON mirrors of the CSIR rich-text fields.
    csirNotableCasesJson?: Record<string, any>;
    csirOpenPlumaBolosPEsJson?: Record<string, any>;
    csirOpenSimTsJson?: Record<string, any>;
    csirOpenAlertMediaMessagesJson?: Record<string, any>;
    csirUpcomingCallsJson?: Record<string, any>;
    csirStaffingFlagsJson?: Record<string, any>;
    csirIssuesFlagsJson?: Record<string, any>;
    // CM-specific fields
    cmEventsInternalMonitoring?: string;
    cmEventsInTriage?: string;
    cmEventsInCoordination?: string;
    cmNotableEscalations?: string;
    cmIssuesBlockers?: string;
    // Tiptap ProseMirror JSON mirrors of the CM rich-text fields.
    cmEventsInternalMonitoringJson?: Record<string, any>;
    cmEventsInTriageJson?: Record<string, any>;
    cmEventsInCoordinationJson?: Record<string, any>;
    cmNotableEscalationsJson?: Record<string, any>;
    cmIssuesBlockersJson?: Record<string, any>;
    // OIM-specific fields
    oimNotableEvents?: string;
    oimBolosPEs?: string;
    oimInquiriesResolutions?: string;
    oimIssuesBlockers?: string;
    oimOtherNotes?: string;
    oimNextOnDuty?: string;
    // Tiptap ProseMirror JSON mirrors of the OIM rich-text fields.
    oimNotableEventsJson?: Record<string, any>;
    oimBolosPEsJson?: Record<string, any>;
    oimInquiriesResolutionsJson?: Record<string, any>;
    oimIssuesBlockersJson?: Record<string, any>;
    oimOtherNotesJson?: Record<string, any>;
    // Alarms-specific fields
    alarmsSlaDefects?: string;
    alarmsBolosPEs?: string;
    alarmsEscalationsNextSteps?: string;
    alarmsIssuesBlockers?: string;
    alarmsNextForgeAudits?: string;
    // Tiptap ProseMirror JSON mirrors of the Alarms rich-text fields.
    alarmsSlaDefectsJson?: Record<string, any>;
    alarmsBolosPEsJson?: Record<string, any>;
    alarmsEscalationsNextStepsJson?: Record<string, any>;
    alarmsIssuesBlockersJson?: Record<string, any>;
    alarmsNextForgeAuditsJson?: Record<string, any>;
    // Events-specific fields
    eventsGeneralUpdates?: string;
    eventsCaseGradingComplete?: boolean;
    eventsCaseGradingNotes?: string;
    eventsCaseReviews?: string;
    // Tiptap ProseMirror JSON mirrors of the Events rich-text fields.
    eventsCaseGradingNotesJson?: Record<string, any>;
    eventsCaseReviewsJson?: Record<string, any>;
    createdBy: string;
    createdAt: string;
    updatedBy?: string;
    updatedAt?: string;
}

export interface CreateShiftPassdownInput {
    category?: string;
    shift?: string;
    notableCases?: string;
    bolosPEs?: string;
    oldestRespondCases?: string;
    oldestReviewCases?: string;
    escalationsNextSteps?: string;
    issuesBlockers?: string;
    nextForgeAudits?: string;
    // Tiptap ProseMirror JSON mirrors of the Global rich-text fields.
    notableCasesJson?: Record<string, any>;
    bolosPEsJson?: Record<string, any>;
    oldestRespondCasesJson?: Record<string, any>;
    oldestReviewCasesJson?: Record<string, any>;
    escalationsNextStepsJson?: Record<string, any>;
    issuesBlockersJson?: Record<string, any>;
    nextForgeAuditsJson?: Record<string, any>;
    csStaffPresent?: number;
    csStaffRequired?: number;
    csirStaffPresent?: number;
    csirStaffRequired?: number;
    priorityAlarmsStaffPresent?: number;
    priorityAlarmsStaffRequired?: number;
    nonPriorityAlarmsStaffPresent?: number;
    nonPriorityAlarmsStaffRequired?: number;
    lsocStaffPresent?: number;
    lsocStaffRequired?: number;
    // CS-specific fields
    openCaseIds?: string;
    csEscalations?: string;
    csBOLOs?: string;
    welfareChecks?: string;
    noteworthyCases?: string;
    // Tiptap ProseMirror JSON mirrors of the CS rich-text fields.
    openCaseIdsJson?: Record<string, any>;
    csEscalationsJson?: Record<string, any>;
    csBOLOsJson?: Record<string, any>;
    welfareChecksJson?: Record<string, any>;
    noteworthyCasesJson?: Record<string, any>;
    abandonedCalls?: number;
    avgSpeedOfAnswer?: number;
    // CSIR-specific fields
    csirNotableCases?: string;
    csirCasesInReview?: number;
    csirOpenPlumaBolosPEs?: string;
    csirOpenSimTs?: string;
    csirOpenAlertMediaMessages?: string;
    csirUpcomingCalls?: string;
    csirStaffingFlags?: string;
    csirIssuesFlags?: string;
    csirNextSupervisor?: string;
    // Tiptap ProseMirror JSON mirrors of the CSIR rich-text fields.
    csirNotableCasesJson?: Record<string, any>;
    csirOpenPlumaBolosPEsJson?: Record<string, any>;
    csirOpenSimTsJson?: Record<string, any>;
    csirOpenAlertMediaMessagesJson?: Record<string, any>;
    csirUpcomingCallsJson?: Record<string, any>;
    csirStaffingFlagsJson?: Record<string, any>;
    csirIssuesFlagsJson?: Record<string, any>;
    // CM-specific fields
    cmEventsInternalMonitoring?: string;
    cmEventsInTriage?: string;
    cmEventsInCoordination?: string;
    cmNotableEscalations?: string;
    cmIssuesBlockers?: string;
    // Tiptap ProseMirror JSON mirrors of the CM rich-text fields.
    cmEventsInternalMonitoringJson?: Record<string, any>;
    cmEventsInTriageJson?: Record<string, any>;
    cmEventsInCoordinationJson?: Record<string, any>;
    cmNotableEscalationsJson?: Record<string, any>;
    cmIssuesBlockersJson?: Record<string, any>;
    // OIM-specific fields
    oimNotableEvents?: string;
    oimBolosPEs?: string;
    oimInquiriesResolutions?: string;
    oimIssuesBlockers?: string;
    oimOtherNotes?: string;
    oimNextOnDuty?: string;
    // Tiptap ProseMirror JSON mirrors of the OIM rich-text fields.
    oimNotableEventsJson?: Record<string, any>;
    oimBolosPEsJson?: Record<string, any>;
    oimInquiriesResolutionsJson?: Record<string, any>;
    oimIssuesBlockersJson?: Record<string, any>;
    oimOtherNotesJson?: Record<string, any>;
    // Alarms-specific fields
    alarmsSlaDefects?: string;
    alarmsBolosPEs?: string;
    alarmsEscalationsNextSteps?: string;
    alarmsIssuesBlockers?: string;
    alarmsNextForgeAudits?: string;
    // Tiptap ProseMirror JSON mirrors of the Alarms rich-text fields.
    alarmsSlaDefectsJson?: Record<string, any>;
    alarmsBolosPEsJson?: Record<string, any>;
    alarmsEscalationsNextStepsJson?: Record<string, any>;
    alarmsIssuesBlockersJson?: Record<string, any>;
    alarmsNextForgeAuditsJson?: Record<string, any>;
    // Events-specific fields
    eventsGeneralUpdates?: string;
    eventsCaseGradingComplete?: boolean;
    eventsCaseGradingNotes?: string;
    eventsCaseReviews?: string;
    // Tiptap ProseMirror JSON mirrors of the Events rich-text fields.
    eventsCaseGradingNotesJson?: Record<string, any>;
    eventsCaseReviewsJson?: Record<string, any>;
}

export async function fetchShiftPassdown(category: string = 'GLOBAL'): Promise<{ items: ShiftPassdownEntry[] }> {
    const res = await apiFetch(`${API_BASE}/passdowns/history?category=${encodeURIComponent(category)}&limit=11`, { headers: getRequestHeaders() });
    if (!res.ok) await handleError(res);
    return await res.json();
}

export async function createShiftPassdown(input: CreateShiftPassdownInput): Promise<Item> {
    const res = await apiFetch(`${API_BASE}/passdowns`, {
        method: 'POST',
        headers: getRequestHeaders('application/json'),
        body: JSON.stringify(input),
    });
    if (!res.ok) await handleError(res);
    return await res.json();
}

export async function updateShiftPassdown(id: string, input: CreateShiftPassdownInput): Promise<Item> {
    const res = await apiFetch(`${API_BASE}/passdowns/${encodeURIComponent(id)}`, {
        method: 'PUT',
        headers: getRequestHeaders('application/json'),
        body: JSON.stringify(input),
    });
    if (!res.ok) await handleError(res);
    return await res.json();
}

export async function deleteShiftPassdown(id: string): Promise<void> {
    const res = await apiFetch(`${API_BASE}/passdowns/${encodeURIComponent(id)}`, {
        method: 'DELETE',
        headers: getRequestHeaders(),
    });
    if (!res.ok) await handleError(res);
}
